import React from "react";
import ImageUpload from "../../components/ImageUpload";
import { DEFAULT_SECTIONS, useGoogleFonts } from "../../components/storefront/Sections";
import { ArrowUp, ArrowDown, Plus, Trash2 } from "lucide-react";

export const FONTS = ["Poppins", "Playfair Display", "Lora", "Montserrat", "DM Serif Display", "Nunito", "Raleway",
  "Cormorant Garamond", "Josefin Sans", "Merriweather", "Space Grotesk", "Dancing Script", "Libre Baskerville", "Work Sans", "Outfit"];
const SECTION_LABELS = { products: "Vouchers", about: "About us", reactions: "Reaction wall", faq: "FAQs", hours: "Opening hours & address", balance: "Balance checker", corporate: "Corporate gifting link" };
const input = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9] bg-white";
const H = ({ children }) => <h5 className="text-xs font-semibold uppercase tracking-wider text-[#8a97a8] mt-5 mb-2">{children}</h5>;

const Choice = ({ value, options, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map(([v, l]) => (
      <button type="button" key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${String(value) === String(v) ? "bg-[#16243b] text-white border-[#16243b]" : "border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9]"}`}>{l}</button>
    ))}
  </div>
);

const FontPick = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-sm font-medium text-[#16243b] mb-1.5">{label}</span>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={testid} style={value ? { fontFamily: `"${value}"` } : {}}>
      <option value="">Default</option>
      {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: `"${f}"` }}>{f}</option>)}
    </select>
  </label>
);

const SectionOrder = ({ sections, onChange }) => {
  const move = (i, dir) => { const s = [...sections]; [s[i], s[i + dir]] = [s[i + dir], s[i]]; onChange(s); };
  return (
    <ul className="space-y-1.5" data-testid="design-sections">
      {sections.map((s, i) => (
        <li key={s.key} className="flex items-center gap-2 bg-[#f6f8fb] rounded-xl px-3 py-2 text-sm">
          <input type="checkbox" checked={s.enabled} onChange={(e) => onChange(sections.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))} data-testid={`design-section-toggle-${s.key}`} />
          <span className="flex-1 text-[#16243b]">{SECTION_LABELS[s.key]}</span>
          <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-[#5a6b80] disabled:opacity-25" data-testid={`design-section-up-${s.key}`}><ArrowUp size={15} /></button>
          <button type="button" disabled={i === sections.length - 1} onClick={() => move(i, 1)} className="p-1 text-[#5a6b80] disabled:opacity-25" data-testid={`design-section-down-${s.key}`}><ArrowDown size={15} /></button>
        </li>
      ))}
    </ul>
  );
};

const ListEditor = ({ rows, fields, onChange, addLabel, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {rows.map((r, i) => (
      <div key={i} className="flex gap-2 items-start">
        {fields.map(([k, ph, area]) => area
          ? <textarea key={k} rows={2} value={r[k] || ""} placeholder={ph} onChange={(e) => onChange(rows.map((x, j) => j === i ? { ...x, [k]: e.target.value } : x))} className={`${input} flex-[2]`} data-testid={`${testid}-${k}-${i}`} />
          : <input key={k} value={r[k] || ""} placeholder={ph} onChange={(e) => onChange(rows.map((x, j) => j === i ? { ...x, [k]: e.target.value } : x))} className={`${input} flex-1`} data-testid={`${testid}-${k}-${i}`} />)}
        <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-red-400 p-2" data-testid={`${testid}-remove-${i}`}><Trash2 size={15} /></button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...rows, {}])} data-testid={`${testid}-add`} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1"><Plus size={14} /> {addLabel}</button>
  </div>
);

export const DesignEditor = ({ design = {}, onChange }) => {
  const d = { ...design, sections: design.sections?.length ? design.sections : DEFAULT_SECTIONS };
  const set = (k) => (v) => onChange({ ...d, [k]: v });
  useGoogleFonts(FONTS);
  return (
    <div className="border-t border-gray-100 pt-4" data-testid="design-editor">
      <h4 className="font-semibold text-[#16243b] text-sm">Page design</h4>
      <p className="text-xs text-[#8a97a8]">Make your voucher hub look like your brand.</p>

      <H>Background</H>
      <div className="flex items-center gap-3 mb-3">
        <input type="color" value={d.bg_color || "#f6f8fb"} onChange={(e) => set("bg_color")(e.target.value)} className="w-11 h-10 rounded cursor-pointer border border-gray-200" data-testid="design-bg-color" />
        <input value={d.bg_color || ""} placeholder="#f6f8fb" onChange={(e) => set("bg_color")(e.target.value)} className={input} data-testid="design-bg-color-text" />
      </div>
      <ImageUpload label="Background image (optional)" value={d.bg_image} onChange={set("bg_image")} />
      {d.bg_image && (
        <label className="block mt-3 text-sm text-[#16243b]">Darken image: {d.bg_overlay || 0}%
          <input type="range" min="0" max="80" step="5" value={d.bg_overlay || 0} onChange={(e) => set("bg_overlay")(parseInt(e.target.value))} className="w-full accent-[#2ec9c9]" data-testid="design-bg-overlay" />
        </label>
      )}

      <H>Fonts</H>
      <div className="grid sm:grid-cols-2 gap-3">
        <FontPick label="Headings" value={d.heading_font} onChange={set("heading_font")} testid="design-heading-font" />
        <FontPick label="Body text" value={d.body_font} onChange={set("body_font")} testid="design-body-font" />
      </div>

      <H>Banner</H>
      <Choice value={d.hero_layout || "centered"} onChange={set("hero_layout")} testid="design-hero-layout" options={[["centered", "Centred"], ["left", "Left-aligned"], ["split", "Split with photo"]]} />
      <div className="mt-2"><Choice value={d.hero_height || "medium"} onChange={set("hero_height")} testid="design-hero-height" options={[["short", "Short"], ["medium", "Medium"], ["tall", "Tall"]]} /></div>

      <H>Voucher cards</H>
      <Choice value={d.card_corners || "rounded"} onChange={set("card_corners")} testid="design-card-corners" options={[["rounded", "Rounded"], ["square", "Square"], ["pill", "Extra round"]]} />
      <div className="mt-2"><Choice value={d.card_columns || 3} onChange={(v) => set("card_columns")(parseInt(v))} testid="design-card-columns" options={[[2, "2 per row"], [3, "3 per row"], [4, "4 per row"]]} /></div>
      <label className="flex items-center gap-2 text-sm text-[#5a6b80] mt-2">
        <input type="checkbox" checked={d.card_show_description !== false} onChange={(e) => set("card_show_description")(e.target.checked)} data-testid="design-card-desc" /> Show descriptions on cards
      </label>

      <H>Page sections (tick to show, arrows to reorder)</H>
      <SectionOrder sections={d.sections} onChange={set("sections")} />

      <H>About us</H>
      <input value={d.about?.title || ""} placeholder="Heading, e.g. Our story" onChange={(e) => set("about")({ ...d.about, title: e.target.value })} className={`${input} mb-2`} data-testid="design-about-title" />
      <textarea rows={3} value={d.about?.text || ""} placeholder="Tell customers about your business" onChange={(e) => set("about")({ ...d.about, text: e.target.value })} className={`${input} mb-2`} data-testid="design-about-text" />
      <ImageUpload label="About photo (optional)" value={d.about?.image} onChange={(v) => set("about")({ ...d.about, image: v })} />

      <H>FAQs</H>
      <ListEditor rows={d.faqs || []} onChange={set("faqs")} addLabel="Add question" testid="design-faqs" fields={[["q", "Question"], ["a", "Answer", true]]} />

      <H>Opening hours & address</H>
      <ListEditor rows={d.hours || []} onChange={set("hours")} addLabel="Add day" testid="design-hours" fields={[["day", "e.g. Mon–Fri"], ["hours", "e.g. 9am – 5pm"]]} />
      <textarea rows={2} value={d.address || ""} placeholder="Address" onChange={(e) => set("address")(e.target.value)} className={`${input} mt-2`} data-testid="design-address" />
      <input value={d.phone || ""} placeholder="Phone" onChange={(e) => set("phone")(e.target.value)} className={`${input} mt-2`} data-testid="design-phone" />
    </div>
  );
};
